/**
 * Zod runtime schemas for LabFlo API responses.
 * Validates data at the boundary between the server and the UI.
 */
import { z } from 'zod';

// ─── Auth ──────────────────────────────────────────────────────────────────

export const UserSchema = z.object({
    id: z.string(),
    username: z.string(),
    name: z.string().optional(),
    role: z.string().optional(),
    is_admin: z.boolean().optional().default(false),
    pharmacy_name: z.string().optional(),
    created_at: z.string().optional(),
});

export const LoginResponseSchema = z.object({
    token: z.string(),
    user: UserSchema,
});

// ─── Tenant / Pharmacy ─────────────────────────────────────────────────────

export const TenantSchema = z.object({
    id: z.string(),
    name: z.string(),
    slug: z.string(),
    custom_subdomain: z.string().optional().nullable(),
    website_url: z.string().optional().nullable(),
    logo_base64: z.string().optional().nullable(),
    address: z.string().optional().nullable(),
    city: z.string().optional().nullable(),
    state: z.string().optional().nullable(),
    phone: z.string().optional().nullable(),
    email: z.string().optional().nullable(),
    is_active: z.boolean().default(true),
    subscription_plan: z.enum(['monthly', 'yearly']).nullable().optional(),
    subscription_status: z.enum(['active', 'expired', 'none']).nullable().optional().default('none'),
    subscription_start: z.string().nullable().optional(),
    subscription_end: z.string().nullable().optional(),
    billing_email: z.string().nullable().optional(),
    created_at: z.string().optional().nullable(),
    user_count: z.number().optional().default(0),
});

export const TenantListItemSchema = z.object({
    name: z.string(),
    slug: z.string(),
    city: z.string().optional().nullable(),
    state: z.string().optional().nullable(),
    phone: z.string().optional().nullable(),
    custom_subdomain: z.string().optional().nullable(),
    website_url: z.string().optional().nullable(),
    logo_base64: z.string().optional().nullable(),
});

export const SubscriptionStatusSchema = z.object({
    status: z.enum(['active', 'expired', 'none']).default('active'),
    plan: z.enum(['monthly', 'yearly']).nullable().optional(),
    end_date: z.string().nullable().optional(),
    pharmacy_name: z.string().optional(),
});

// ─── Tasks ─────────────────────────────────────────────────────────────────

export const TaskSchema = z.object({
    id: z.string(),
    title: z.string(),
    description: z.string().optional().nullable(),
    category_id: z.string().optional().nullable(),
    category_name: z.string().optional().nullable(),
    priority: z.enum(['high', 'medium', 'low']).default('medium'),
    status: z.enum(['todo', 'in_progress', 'completed']).default('todo'),
    due_date: z.string().optional().nullable(),
    due_time: z.string().optional().nullable(),
    is_overdue: z.boolean().optional().default(false),
    assigned_to: z.string().optional().nullable(),
    created_by: z.string().optional().nullable(),
    created_at: z.string().optional(),
});

// ─── Reports ───────────────────────────────────────────────────────────────

export const ReportSummarySchema = z.object({
    total: z.number().optional().default(0),
    todo: z.number().optional().default(0),
    in_progress: z.number().optional().default(0),
    completed: z.number().optional().default(0),
    overdue: z.number().optional().default(0),
});

export const ReportDataSchema = z.object({
    summary: ReportSummarySchema,
    tasks: z.array(TaskSchema).default([]),
    by_priority: z.object({
        high: z.number().optional().default(0),
        medium: z.number().optional().default(0),
        low: z.number().optional().default(0),
    }).default({ high: 0, medium: 0, low: 0 }),
    by_category: z.record(z.string(), z.number()).default({}),
});

// ─── Audit ────────────────────────────────────────────────────────────────

export const AuditEntrySchema = z.object({
    id: z.string(),
    label: z.string().optional(),
    task_title: z.string().optional().nullable(),
    task_id: z.string().optional().nullable(),
    new_value: z.string().optional().nullable(),
    old_value: z.string().optional().nullable(),
    changed_by_username: z.string().optional().nullable(),
    changed_at: z.string().optional().nullable(),
});

// ─── TypeScript inferred types ────────────────────────────────────────────

export type UserType = z.infer<typeof UserSchema>;
export type TenantType = z.infer<typeof TenantSchema>;
export type TenantListItemType = z.infer<typeof TenantListItemSchema>;
export type SubscriptionStatusType = z.infer<typeof SubscriptionStatusSchema>;
export type TaskType = z.infer<typeof TaskSchema>;
export type ReportDataType = z.infer<typeof ReportDataSchema>;
export type AuditEntryType = z.infer<typeof AuditEntrySchema>;

// ─── Safe parse helper ────────────────────────────────────────────────────

/**
 * Safely validates API response data against a Zod schema.
 * On failure, logs a warning and returns the raw data (graceful degradation).
 */
export function safeValidate<T>(schema: z.ZodType<T>, data: unknown, _label?: string): T {
    const result = schema.safeParse(data);
    if (!result.success) {
        return data as T;
    }
    return result.data;
}
